/**
 * Apply AI Findings Confirmation Dialog
 * Shows a preview of what will be applied to the ticket before confirming
 */

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Loader2,
  Lightbulb,
  AlertTriangle,
  CheckCircle2,
  Tag,
  ArrowRight,
  Shield,
  AlertCircle,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import type { QmsAiInvestigation, QmsRiskLevel } from '../../../types/qms.types';

interface ApplyAiFindingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  investigation: QmsAiInvestigation;
  onConfirm: () => void;
  isPending: boolean;
}

function getRiskLevelBadge(level: QmsRiskLevel) {
  const config: Record<QmsRiskLevel, { className: string; icon: React.ReactNode }> = {
    critical: {
      className: 'bg-red-100 text-red-800 border-red-200',
      icon: <AlertCircle className="h-3 w-3" />,
    },
    high: {
      className: 'bg-orange-100 text-orange-800 border-orange-200',
      icon: <AlertTriangle className="h-3 w-3" />,
    },
    medium: {
      className: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      icon: <Shield className="h-3 w-3" />,
    },
    low: {
      className: 'bg-green-100 text-green-800 border-green-200',
      icon: <CheckCircle2 className="h-3 w-3" />,
    },
  };
  return config[level] || config.medium;
}

export function ApplyAiFindingsDialog({
  open,
  onOpenChange,
  investigation,
  onConfirm,
  isPending,
}: ApplyAiFindingsDialogProps) {
  const findings = investigation.findings;

  if (!findings) return null;

  const hasRootCauses = findings.potentialRootCauses && findings.potentialRootCauses.length > 0;
  const hasActions = findings.recommendedActions && findings.recommendedActions.length > 0;
  const hasRiskAssessment = !!findings.riskAssessment;
  const hasPriority = !!findings.suggestedPriority;
  const hasTags = findings.suggestedTags && findings.suggestedTags.length > 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Lightbulb className="h-5 w-5 text-purple-600" />
            Apply AI Findings to Ticket
          </DialogTitle>
          <DialogDescription>
            Review what will be applied to the ticket. This action will update the following fields:
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Root Cause Analysis (Investigation) Field - Summary & Risk Assessment */}
          {(findings.summary || hasRiskAssessment) && (
            <div className="space-y-2">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <ArrowRight className="h-4 w-4 text-purple-500" />
                Root Cause Analysis (Investigation)
              </div>
              <div className="ml-6 p-3 bg-purple-50 rounded-md border border-purple-200 space-y-3 text-sm">
                <p className="text-xs text-purple-700 italic">
                  The following will be appended to your Investigation field:
                </p>

                {/* Summary Preview */}
                {findings.summary && (
                  <div>
                    <p className="font-semibold text-gray-800">AI Investigation Summary:</p>
                    <p className="text-gray-600 mt-1">{findings.summary}</p>
                  </div>
                )}

                {/* Risk Assessment Preview */}
                {hasRiskAssessment && (
                  <div>
                    <p className="font-semibold text-gray-800">Risk Assessment:</p>
                    <div className="mt-1 space-y-1">
                      <Badge
                        variant="outline"
                        className={cn(
                          'capitalize',
                          getRiskLevelBadge(findings.riskAssessment!.level).className
                        )}
                      >
                        {getRiskLevelBadge(findings.riskAssessment!.level).icon}
                        <span className="ml-1">{findings.riskAssessment!.level} RISK</span>
                      </Badge>
                      <p className="text-gray-600">{findings.riskAssessment!.reasoning}</p>
                      {findings.riskAssessment!.factors && findings.riskAssessment!.factors.length > 0 && (
                        <div className="mt-2">
                          <p className="font-medium text-gray-700 text-xs">Risk Factors:</p>
                          <ul className="list-disc list-inside text-gray-600 text-xs">
                            {findings.riskAssessment!.factors.map((factor, i) => (
                              <li key={i}>{factor}</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Investigation Findings Field - Root Causes & Actions */}
          {(hasRootCauses || hasActions) && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <ArrowRight className="h-4 w-4 text-purple-500" />
                Investigation Findings
              </div>
              <div className="ml-6 p-3 bg-gray-50 rounded-md text-sm text-gray-600">
                {hasRootCauses && (
                  <p className="text-xs text-gray-500">
                    {findings.potentialRootCauses.length} root cause{findings.potentialRootCauses.length > 1 ? 's' : ''} identified
                  </p>
                )}
                {hasActions && (
                  <p className="text-xs text-gray-500">
                    {findings.recommendedActions.length} recommended action{findings.recommendedActions.length > 1 ? 's' : ''}
                  </p>
                )}
              </div>
            </div>
          )}

          {/* Priority */}
          {hasPriority && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <ArrowRight className="h-4 w-4 text-purple-500" />
                Priority
              </div>
              <div className="ml-6">
                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                  Priority {findings.suggestedPriority}
                </Badge>
              </div>
            </div>
          )}

          {/* Tags */}
          {hasTags && (
            <div className="space-y-1">
              <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
                <ArrowRight className="h-4 w-4 text-purple-500" />
                <Tag className="h-4 w-4" />
                Tags
              </div>
              <div className="ml-6 flex flex-wrap gap-1">
                {findings.suggestedTags!.map((tag, index) => (
                  <Badge
                    key={index}
                    variant="secondary"
                    className="bg-purple-100 text-purple-800 text-xs"
                  >
                    {tag}
                  </Badge>
                ))}
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            onClick={onConfirm}
            disabled={isPending}
            className="bg-purple-600 hover:bg-purple-700"
          >
            {isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Applying...
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4 mr-2" />
                Apply to Ticket
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
